import { formatMoney } from "utils";
import { createGloboAvailability, isGloboContentMutation } from "globo-product-availability";

const APP_SELECTOR = ".gpo-app";
const MOUNT_SELECTOR = ".gpo-product-variants";
const MARKER_SELECTOR = "[data-raaw-saving]";
const OPTION_ITEM_SELECTOR = ".gpo-ov-item";
const LINKED_VARIANT_SELECTOR =
  "input[gpo-data-product-handle][gpo-data-variant-id]";
const READY_ATTRIBUTE = "data-raaw-saving-ready";
const READY_HELP_TEXT_CLASS = "gpo-ov-helptext--raaw-saving-ready";
const READY_OPTION_ITEM_CLASS = "gpo-ov-item--raaw-saving-ready";
const PRODUCT_DATA_SECTION_ID = "raaw-linked-product-data";
const PRODUCT_DATA_SELECTOR = "[data-raaw-linked-product-data]";
const DELIVERY_CHANGE_EVENT = "raaw:globo-delivery-change";

const linkedProductRequests = new Map();
const observedApps = new WeakSet();
const deliveryStates = new WeakMap();

function getLinkedProductData(handle) {
  if (!linkedProductRequests.has(handle)) {
    const root = window.Shopify?.routes?.root || "/";
    const url = `${root}products/${encodeURIComponent(handle)}?section_id=${PRODUCT_DATA_SECTION_ID}`;

    const request = fetch(url, {
      headers: { Accept: "text/html" },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load option product (${response.status})`);
        }

        return response.text();
      })
      .then((html) => {
        const documentFragment = new DOMParser().parseFromString(
          html,
          "text/html",
        );
        const dataElement = documentFragment.querySelector(
          PRODUCT_DATA_SELECTOR,
        );

        if (!dataElement) {
          throw new Error("Option product data is missing");
        }

        return JSON.parse(dataElement.textContent);
      });

    linkedProductRequests.set(handle, request);
  }

  return linkedProductRequests.get(handle);
}

function getBadge(marker) {
  const badge = marker.closest(".raaw-discount-badge") || marker;
  badge.classList.add("raaw-discount-badge");
  return badge;
}

function hideBadge(marker, state) {
  const badge = getBadge(marker);
  badge.removeAttribute(READY_ATTRIBUTE);
  badge.closest(".gpo-ov-helptext")?.classList.remove(READY_HELP_TEXT_CLASS);
  marker.closest(OPTION_ITEM_SELECTOR)?.classList.remove(READY_OPTION_ITEM_CLASS);
  marker.dataset.raawSavingState = state;
}

async function populateMarker(marker) {
  if (marker.dataset.raawSavingState) return;

  const optionItem = marker.closest(OPTION_ITEM_SELECTOR);
  const linkedVariant = optionItem?.querySelector(LINKED_VARIANT_SELECTOR);

  // Globo can build an option over multiple mutations. Leave the marker
  // untouched until its linked input is available so a later scan can retry.
  if (!linkedVariant) return;

  const handle = linkedVariant.getAttribute("gpo-data-product-handle");
  const variantId = linkedVariant.getAttribute("gpo-data-variant-id");

  if (!handle || !/^\d+$/.test(variantId || "")) {
    hideBadge(marker, "invalid");
    return;
  }

  marker.dataset.raawSavingState = "loading";

  try {
    const product = await getLinkedProductData(handle);
    const variant = product.variants?.find(
      ({ id }) => String(id) === variantId,
    );
    const price = Number(variant?.price);
    const compareAtPrice = Number(variant?.compare_at_price);

    if (
      !Number.isFinite(price) ||
      !Number.isFinite(compareAtPrice) ||
      compareAtPrice <= price
    ) {
      hideBadge(marker, "unavailable");
      return;
    }

    const moneyFormat =
      window.theme?.money_product_price_format || window.theme?.money_format;

    if (!moneyFormat) {
      hideBadge(marker, "unavailable");
      return;
    }

    marker.textContent = formatMoney(compareAtPrice - price, moneyFormat);
    marker.dataset.raawSavingState = "ready";
    const badge = getBadge(marker);
    badge.setAttribute(READY_ATTRIBUTE, "true");
    badge
      .closest(".gpo-ov-helptext")
      ?.classList.add(READY_HELP_TEXT_CLASS);
    optionItem.classList.add(READY_OPTION_ITEM_CLASS);
  } catch (error) {
    hideBadge(marker, "error");
    console.warn("Unable to populate Globo option saving badge.", error);
  }
}

function scanMarkers(app) {
  app.querySelectorAll(MARKER_SELECTOR).forEach(populateMarker);
}

function isActiveLinkedVariant(input) {
  if (input.closest(".gpo-hidden, [hidden]")) return false;

  // data-clo-display describes the configured conditional action, not the
  // element's current state. Globo hides inactive conditional groups with
  // display:none, while a collapsed but active group remains display:block.
  const app = input.closest(APP_SELECTOR);
  for (
    let element = input.parentElement;
    element && element !== app;
    element = element.parentElement
  ) {
    if (
      element.hasAttribute("data-clo-display") &&
      window.getComputedStyle(element).display === "none"
    ) {
      return false;
    }
  }

  return true;
}

function getSelectedLinkedVariants(app) {
  const selections = new Map();

  app
    .querySelectorAll(`${LINKED_VARIANT_SELECTOR}:checked`)
    .forEach((input) => {
      if (!isActiveLinkedVariant(input)) return;

      const handle = input.getAttribute("gpo-data-product-handle");
      const variantId = input.getAttribute("gpo-data-variant-id");

      if (!handle || !/^\d+$/.test(variantId || "")) return;
      selections.set(`${handle}:${variantId}`, { handle, variantId });
    });

  return Array.from(selections.values()).sort((a, b) =>
    `${a.handle}:${a.variantId}`.localeCompare(`${b.handle}:${b.variantId}`),
  );
}

function getDeliveryEstimateContainer(app) {
  const section = app.closest("[data-section-id]");
  return section?.querySelector('[id^="delivery-estimate-"]') || null;
}

function publishGloboDeliveryWeeks(app, weeks) {
  const deliveryEstimateContainer = getDeliveryEstimateContainer(app);
  if (!deliveryEstimateContainer) return;

  deliveryEstimateContainer.dataset.globoWeeksToDelivery = String(weeks);
  deliveryEstimateContainer.dispatchEvent(
    new CustomEvent(DELIVERY_CHANGE_EVENT, {
      bubbles: true,
      detail: { weeks },
    }),
  );
}

async function updateGloboDeliveryEstimate(app) {
  const state = deliveryStates.get(app);
  if (!state) return;

  const selections = getSelectedLinkedVariants(app);
  const signature = selections
    .map(({ handle, variantId }) => `${handle}:${variantId}`)
    .join("|");

  if (signature === state.signature) return;

  state.signature = signature;
  state.version += 1;
  const version = state.version;

  if (!selections.length) {
    publishGloboDeliveryWeeks(app, 0);
    return;
  }

  try {
    const weeks = await Promise.all(
      selections.map(async ({ handle, variantId }) => {
        const product = await getLinkedProductData(handle);
        const variant = product.variants?.find(
          ({ id }) => String(id) === variantId,
        );
        const parsedWeeks = Number.parseInt(
          variant?.weeks_to_delivery || "0",
          10,
        );
        return Number.isNaN(parsedWeeks) ? 0 : parsedWeeks;
      }),
    );

    if (version !== state.version) return;
    publishGloboDeliveryWeeks(app, Math.max(0, ...weeks));
  } catch (error) {
    if (version !== state.version) return;
    console.warn("Unable to update Globo option delivery estimate.", error);
  }
}

function scheduleGloboDeliveryUpdate(app) {
  const state = deliveryStates.get(app);
  if (!state || state.updateScheduled) return;

  state.updateScheduled = true;
  window.requestAnimationFrame(() => {
    state.updateScheduled = false;
    updateGloboDeliveryEstimate(app);
  });
}

function observeApp(app) {
  if (observedApps.has(app)) return;

  observedApps.add(app);
  const availability = createGloboAvailability(app);
  deliveryStates.set(app, {
    signature: null,
    version: 0,
    updateScheduled: false,
  });
  scanMarkers(app);
  scheduleGloboDeliveryUpdate(app);

  app.addEventListener("change", () => {
    scheduleGloboDeliveryUpdate(app);
  });

  let scanScheduled = false;
  const observer = new MutationObserver((mutations) => {
    if (!mutations.some(isGloboContentMutation)) return;
    availability?.schedule();
    if (scanScheduled) return;

    scanScheduled = true;
    window.requestAnimationFrame(() => {
      scanScheduled = false;
      scanMarkers(app);
      scheduleGloboDeliveryUpdate(app);
    });
  });

  observer.observe(app, {
    attributes: true,
    attributeFilter: ["checked", "disabled", "data-clo-display", "hidden", "data-unavailable", "style", "class", "value"],
    childList: true,
    subtree: true,
  });
}

function findApps(root = document) {
  if (root instanceof Element && root.matches(APP_SELECTOR)) {
    observeApp(root);
  }

  root.querySelectorAll?.(APP_SELECTOR).forEach(observeApp);
}

function initialize() {
  if (
    !document.querySelector(MOUNT_SELECTOR) &&
    !document.querySelector(APP_SELECTOR)
  ) {
    return;
  }

  findApps();

  if (document.querySelector(APP_SELECTOR)) return;

  // Globo is loaded asynchronously. Observe only until its app root exists;
  // all subsequent observation is scoped to that root.
  const discoveryObserver = new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node instanceof Element) findApps(node);
      });
    });

    if (document.querySelector(APP_SELECTOR)) {
      discoveryObserver.disconnect();
    }
  });

  discoveryObserver.observe(document.body, { childList: true, subtree: true });
  window.setTimeout(() => discoveryObserver.disconnect(), 15000);
}

initialize();
